// ═══════════════════════════════════════════════════════════════
// XYQAR — Client Account (GDPR Art.17+20 self-serve + MFA + email)
// ═══════════════════════════════════════════════════════════════
import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet, apiPost } from '../lib/api';
import {
  PageTitle, Section, Btn, Kpi,
} from '../components/ui-kit';
import { ShieldCheck, Mail, Trash2, Download, KeyRound } from 'lucide-react';

interface EmailStatus { email: string; email_verified: boolean }
interface MfaStatus   { enrolled: boolean; authenticators: { id: string; type: string }[] }

export default function Account() {
  const { getToken, principal } = useXyqarAuth();

  const [emailStatus,  setEmailStatus]  = React.useState<EmailStatus | null>(null);
  const [mfaStatus,    setMfaStatus]    = React.useState<MfaStatus | null>(null);
  const [busy,         setBusy]         = React.useState<string | null>(null);
  const [msg,          setMsg]          = React.useState<string | null>(null);
  const [msgKind,      setMsgKind]      = React.useState<'ok' | 'err'>('ok');

  const tok = React.useCallback(() => getToken().catch(() => ''), [getToken]);

  const flash = (text: string, kind: 'ok' | 'err' = 'ok') => {
    setMsg(text); setMsgKind(kind);
    setTimeout(() => setMsg(null), 5000);
  };

  React.useEffect(() => {
    (async () => {
      try {
        const t = await tok();
        const [es, ms] = await Promise.all([
          apiGet<EmailStatus>('/api/v1/auth/email/status', t),
          apiGet<MfaStatus>('/api/v1/auth/mfa/status', t).catch(() => ({ enrolled: false, authenticators: [] })),
        ]);
        setEmailStatus(es);
        setMfaStatus(ms);
      } catch { /* graceful */ }
    })();
  }, [tok]);

  const sendVerification = async () => {
    setBusy('email'); setMsg(null);
    try {
      const t = await tok();
      await apiPost('/api/v1/auth/email/send-verification', {}, t);
      flash('Verification email sent — check your inbox.');
    } catch (e: any) {
      flash(e.message ?? 'Error sending verification', 'err');
    } finally { setBusy(null); }
  };

  const requestExport = async () => {
    setBusy('export'); setMsg(null);
    try {
      const t = await tok();
      const blob = await fetch('/api/v1/me/export', { headers: { Authorization: `Bearer ${t}` } })
        .then((r) => { if (!r.ok) throw new Error('export failed'); return r.blob(); });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `xyqar-data-export-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
      flash('Data export downloaded.');
    } catch (e: any) {
      flash(e.message ?? 'Export failed', 'err');
    } finally { setBusy(null); }
  };

  const requestErasure = async () => {
    if (!window.confirm(
      'This will permanently delete your account, all mission history and voice data. This action cannot be undone. Confirm?'
    )) return;
    setBusy('delete'); setMsg(null);
    try {
      const t = await tok();
      await fetch('/api/v1/me', { method: 'DELETE', headers: { Authorization: `Bearer ${t}` } })
        .then((r) => { if (!r.ok) throw new Error('Erasure request failed'); });
      flash('Your account has been scheduled for deletion. You will be logged out shortly.');
      const redirectToHome = () => window.location.assign('https://xyqar.com');
      window.setTimeout(redirectToHome, 3000);
    } catch (e: any) {
      flash(e.message ?? 'Deletion failed', 'err');
    } finally { setBusy(null); }
  };

  return (
    <div>
      <PageTitle kicker="My Account" title={principal?.email ?? 'Account settings'} />

      {msg && (
        <div className={`mb-6 rounded-md px-4 py-3 text-sm font-mono ${msgKind === 'ok' ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/40' : 'bg-red-900/30 text-red-300 border border-red-700/40'}`}>
          {msg}
        </div>
      )}

      {/* ── Email ── */}
      <Section title="Email verification" hint="Xyqar sends the verification email directly">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <Kpi
            accent={emailStatus?.email_verified ? 'green' : 'amber'}
            label="Status"
            value={emailStatus?.email_verified ? 'Verified' : 'Unverified'}
            hint={emailStatus?.email ?? principal?.email ?? '—'}
          />
          <Kpi label="Provider" value="Xyqar Identity" hint="Enterprise SSO — MFA enforced" />
        </div>
        {!emailStatus?.email_verified && (
          <Btn
            primary
            onClick={sendVerification}
            disabled={busy === 'email'}
            icon={<Mail className="h-4 w-4" />}
          >
            {busy === 'email' ? 'Sending…' : 'Resend verification email'}
          </Btn>
        )}
        {emailStatus?.email_verified && (
          <p className="flex items-center gap-2 text-emerald-400 text-sm">
            <ShieldCheck className="h-4 w-4" /> Email confirmed
          </p>
        )}
      </Section>

      {/* ── MFA ── */}
      <Section title="Multi-factor authentication (TOTP)" hint="Scan with your authenticator app">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <Kpi
            accent={mfaStatus?.enrolled ? 'green' : 'amber'}
            label="MFA status"
            value={mfaStatus?.enrolled ? 'Enrolled' : 'Not enrolled'}
            hint={mfaStatus?.enrolled ? `${mfaStatus.authenticators.length} device(s)` : 'Recommended for executive access'}
          />
        </div>
        {!mfaStatus?.enrolled && (
          <Btn primary icon={<KeyRound className="h-4 w-4" />} onClick={() => window.open('https://manage.auth0.com/', '_blank')}>
            Enroll TOTP device
          </Btn>
        )}
      </Section>

      {/* ── GDPR Art.20 — export ── */}
      <Section title="Export your data" hint="GDPR Art.20 portability — machine-readable JSON of all your data">
        <p className="text-white/60 text-sm mb-4">
          Includes your account, missions, proposals, voice session metadata and audit events.
          Does not include raw voice recordings (never stored).
        </p>
        <Btn
          onClick={requestExport}
          disabled={busy === 'export'}
          icon={<Download className="h-4 w-4" />}
        >
          {busy === 'export' ? 'Preparing…' : 'Download my data (JSON)'}
        </Btn>
      </Section>

      {/* ── GDPR Art.17 — RTBF ── */}
      <Section title="Delete my account" hint="GDPR Art.17 — Right to erasure. Irreversible.">
        <p className="text-white/60 text-sm mb-4">
          Permanently deletes your email, display name, profile and all associated data.
          Mission history is anonymised. Voice transcripts are wiped. Identity account is removed.
          This action cannot be undone.
        </p>
        <Btn
          onClick={requestErasure}
          disabled={busy === 'delete'}
          icon={<Trash2 className="h-4 w-4" />}
          className="border-red-700/50 text-red-400 hover:border-red-600 hover:text-red-300"
        >
          {busy === 'delete' ? 'Deleting…' : 'Delete my account permanently'}
        </Btn>
      </Section>
    </div>
  );
}
